import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { AlarmClock, UserRoundX } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import type { AbsenceCalendarRow, AbsenceListItem, LeaveBalanceResponse } from '@ip/shared-types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { AppShellContext } from '@/components/layout/AppShell';
import { HrTabs } from '@/components/hr/HrTabs';
import { BalanceHero } from '@/components/hr/BalanceHero';
import { MonthCalendar } from '@/components/hr/MonthCalendar';
import { RequestSheet } from '@/components/hr/RequestSheet';
import { MyRequestsList } from '@/components/hr/MyRequestsList';
import { WhosOut } from '@/components/hr/WhosOut';
import { ComplianceCard } from '@/components/hr/ComplianceCard';
import { useHrMe, useHolidays } from '@/components/hr/useHrData';
import { useHrApi, SessionExpiredError } from '@/services/api';
import { addDays, monthStart, todayYmd } from '@/lib/hr';
import { t } from '@/i18n';

/**
 * Employee self-service (Task HR4 surface 1): the balance hero, a
 * drag-to-request month calendar, "my requests" and "who's out next 7 days".
 */
export function HrPage() {
  const { nip } = useOutletContext<AppShellContext>();
  const { hrMe, loading: meLoading } = useHrMe(nip);
  const { leaveBalance, listAbsences, absenceCalendar, cancelAbsence, payAlarm } = useHrApi();

  const today = todayYmd();
  const currentYear = Number(today.slice(0, 4));
  const [month, setMonth] = useState(() => monthStart(today));
  const monthYear = Number(month.slice(0, 4));
  const holidays = useHolidays(nip, [monthYear, monthYear + 1, currentYear]);

  const [balance, setBalance] = useState<LeaveBalanceResponse | null>(null);
  const [myAbsences, setMyAbsences] = useState<AbsenceListItem[]>([]);
  const [teamRows, setTeamRows] = useState<AbsenceCalendarRow[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetRange, setSheetRange] = useState<{ from: string; to: string } | null>(null);

  const employee = hrMe?.employee ?? null;
  const employeeId = employee?.id ?? null;
  const isUop = employee?.employmentType === 'employment_contract';
  const norm = employee?.dailyNormHours ?? 8;

  useEffect(() => {
    if (!employeeId) {
      if (hrMe) setDataLoading(false);
      return;
    }
    let mounted = true;
    setDataLoading(true);
    (async () => {
      const [balanceRes, absencesRes, teamRes] = await Promise.allSettled([
        isUop ? leaveBalance(nip, employeeId, currentYear) : Promise.resolve(null),
        listAbsences(nip, { employeeId }),
        absenceCalendar(nip, today, addDays(today, 7), 'team'),
      ]);
      if (!mounted) return;
      setBalance(balanceRes.status === 'fulfilled' ? balanceRes.value : null);
      setMyAbsences(absencesRes.status === 'fulfilled' ? absencesRes.value : []);
      setTeamRows(teamRes.status === 'fulfilled' ? teamRes.value : []);
      const failure = [balanceRes, absencesRes, teamRes].find(
        (r): r is PromiseRejectedResult =>
          r.status === 'rejected' && !(r.reason instanceof SessionExpiredError),
      );
      if (failure) toast.error(t('hr.common.loadFailed'));
      setDataLoading(false);
    })();
    return () => {
      mounted = false;
    };
    // `today` is intentionally not a dep — it only changes at midnight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nip, employeeId, isUop, hrMe, currentYear, refreshKey, leaveBalance, listAbsences, absenceCalendar]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const yearAbsences = (type: AbsenceListItem['type']) =>
    myAbsences
      .filter((a) => a.type === type && a.status === 'approved' && a.dateFrom.startsWith(String(currentYear)))
      .reduce((sum, a) => sum + a.days, 0);

  const handleSelectRange = (from: string, to: string) => {
    setSheetRange({ from, to });
    setSheetOpen(true);
  };

  const handleCancel = async (id: string) => {
    try {
      await cancelAbsence(nip, id);
      toast.success(t('hr.myRequests.cancelled'));
      refresh();
    } catch (error) {
      if (!(error instanceof SessionExpiredError)) {
        toast.error(error instanceof Error ? error.message : String(error));
      }
    }
  };

  if (meLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-44 w-full" />
        <div className="grid gap-4 lg:grid-cols-3">
          <Skeleton className="h-96 lg:col-span-2" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (hrMe && !employee) {
    return (
      <div className="flex flex-col gap-4">
        <HrTabs nip={nip} hrMe={hrMe} />
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <UserRoundX className="text-muted-foreground size-5" />
              {t('hr.notLinked.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-muted-foreground space-y-2 text-sm">
            <p>{t('hr.notLinked.body')}</p>
            <p>{t('hr.notLinked.action')}</p>
            {hrMe.isHrAdmin && <p>{t('hr.notLinked.adminHint')}</p>}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <HrTabs nip={nip} hrMe={hrMe} />

      <BalanceHero
        employmentType={employee?.employmentType ?? null}
        balance={balance}
        loading={dataLoading}
        norm={norm}
        year={currentYear}
        sickDaysYtd={yearAbsences('sick')}
        unavailabilityDaysYtd={yearAbsences('unavailability_b2b')}
        remoteDaysYtd={yearAbsences('remote_work')}
        onRequest={() => handleSelectRange(today, today)}
      />

      <div className="grid items-start gap-4 lg:grid-cols-3">
        <div className="min-w-0 lg:col-span-2">
          <MonthCalendar
            month={month}
            onMonthChange={setMonth}
            holidays={holidays}
            absences={myAbsences}
            onSelectRange={handleSelectRange}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          <ComplianceCard nip={nip} />
          <WhosOut rows={teamRows} selfEmployeeId={employeeId} from={today} to={addDays(today, 7)} />
          <MyRequestsList absences={myAbsences} onCancel={handleCancel} />
        </div>
      </div>

      {/* B2B contractors raise per-invoice alarms on the "Moje faktury" tab. */}
      {employee && !employee.hasContractor && (
        <div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-rose-600 dark:hover:text-rose-400">
                <AlarmClock className="size-3.5" />
                {t('hr.payAlarm.button')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('hr.payAlarm.confirm')}</AlertDialogTitle>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-white hover:bg-destructive/90"
                  onClick={async () => {
                    try {
                      await payAlarm(nip);
                      toast.success(t('hr.payAlarm.sent'));
                    } catch (error) {
                      if (!(error instanceof SessionExpiredError)) toast.error((error as Error).message);
                    }
                  }}
                >
                  {t('hr.payAlarm.button')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {hrMe && (
        <RequestSheet
          nip={nip}
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          hrMe={hrMe}
          balance={balance}
          holidays={holidays}
          myAbsences={myAbsences}
          initialRange={sheetRange}
          onSubmitted={refresh}
        />
      )}
    </div>
  );
}
